import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography, Grid } from '@material-ui/core';
import DataGrid from 'views/containers/datagrid';
import { useMatchesTabColumns } from './MatchesTabColumns';
import './MatchesTab.css';
import MultiSelectDropdown from 'views/components/careConnectPage/MultiSelectDropdown';
import AutocompleteSearch from 'views/components/careConnectPage/AutocompleteSearch';
import FilterListIcon from '@material-ui/icons/FilterList';
import AdditionalFilters from 'components/sidebar/CareConnect/additionalFilters/AdditionalFilters';
import FilterChips from 'components/sidebar/CareConnect/FilterChips/FilterChips';
import { IClinicOptionsList } from 'model/clinic';
import {
  ISchedulingShiftDropdownOptions,
  IProviderScheduleSummary,
  IShift,
  IClientRow,
  ISetShiftRow,
  IAdditionalRow,
  IOptionType,
  IAppliedFilters,
  IGraphQLClientData,
  IFilterData,
  IAutocompleteEvent,
  IGraphQLSchedule,
  IBTData,
  ICreateCasePayload
} from 'model/v2';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';
import { GET_ATTENDEES } from 'api/graphql/v2/queries/Attendees';
import { GET_WAITLIST_INITIAL_LOAD_DATA } from 'api/graphql/queries/careplan';
import {
  GET_SHIFT_DROPDOWN_OPTIONS,
  GET_PROVIDER_SCHEDULE_SUMMARY
} from 'api/graphql/v2/queries/Providers';
import { GET_CARECONNECT_CLIENTS } from 'api/graphql/queries/careconnect';
import { alphabeticalSort } from 'utils/sort';
import { DayAvailabilityBlock } from 'model/v2/availabilityTemplate';
import { getIntersectingTimeBlocks } from 'components/AvailabilityTemplateModalForm/SetShiftForm/overlapUtils';
import { ROUTE_PARAMS, ROUTE_PATHS } from 'utils/constants/routes';
import { ABA_APPT_IDS_FOR_MATCHES } from 'utils/constants/appointmentsTypes';
import { convertTimeToLocalBrowser } from 'utils/mappers/form';
import { CREATE_SALESFORCE_CASE_FROM_CARE_CONNECT } from 'api/graphql/v2/mutations/BlockTemplate';
import { toast } from 'react-toastify';
import { RECOMMENDED_HOURS_RANGE } from 'utils/constants/calendarFilters';
import { btLabelOf, btLegalLabelOf } from '../btLabel';

// Transform GraphQL client data to table format
const transformClientDataToTableData = (
  graphqlData: IGraphQLClientData[]
): IClientRow[] => {
  if (!graphqlData || !Array.isArray(graphqlData)) return [];

  return graphqlData.map(item => {
    const acceptedBlock =
      item.acceptedSchedule?.map(
        (schedule: IGraphQLSchedule) =>
          `${schedule.dayOfWeek}\n${schedule.times}`
      ) || [];

    // Display uses the *ForUI combined view (the plain remainingSchedule
    // excludes opening-soon blocks, which feed the scheduling automation via
    // remainingScheduleRaw below).
    const remainingBlockItems =
      (item.remainingScheduleForUI || item.remainingSchedule)?.map(
        (schedule: IGraphQLSchedule) =>
          ({ text: `${schedule.dayOfWeek}\n${schedule.times}`, opensOn: schedule.opensOn, hasNonBTBooking: schedule.hasNonBTBooking })
      ) || [];

    const remainingBlocks = remainingBlockItems.map((item: any) => item.text);

    return {
      client: `${item.client?.name || 'Unknown'}`,
      acceptedBlock: acceptedBlock.join('\n'),
      remainingBlocks: remainingBlocks.join('\n'),
      remainingBlockItems,
      remainingBlocksRaw: item.remainingScheduleRaw,
      status: item.status || '',
      waitlistedReason: item.waitlistedReason || 'N/A',
      recommended: item.recommended || 0,
      clientId: item.client?.id?.toString() || '',
      name: item.client?.name || 'Unknown',
      link: true,
      insurancePlan: item.insurancePlan || null,
      salesForceId: item.client?.salesForceId!,
      clinic: item.clinic
        ? {
            ...item.clinic,
            id: Number(item.clinic.id),
            address: item.clinic.address
              ? {
                  ...item.clinic.address,
                  id: 0 // Default ID since it's not provided in GraphQL response
                }
              : undefined
          }
        : undefined
    };
  });
};

export default function MatchesTab() {
  // Client table state
  const [clientPage, setClientPage] = useState(0);
  const [clientData, setClientData] = useState<IClientRow[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientHasMore, setClientHasMore] = useState(true);
  const [clientTotalCount, setClientTotalCount] = useState(0);
  // const [clientId, setClientId] = useState<number | null>(null);

  // Set Shift table state
  const [setShiftPage, setSetShiftPage] = useState(0);
  const [setShiftData, setSetShiftData] = useState<ISetShiftRow[]>([]);
  const [setShiftLoading, setSetShiftLoading] = useState(false);
  const [setShiftHasMore, setSetShiftHasMore] = useState(true);

  // Additional table state
  const [additionalPage, setAdditionalPage] = useState(0);
  const [additionalData, setAdditionalData] = useState<IAdditionalRow[]>([]);
  const [additionalLoading, setAdditionalLoading] = useState(false);
  const [additionalHasMore, setAdditionalHasMore] = useState(true);

  // Refs to track if we're currently fetching to prevent duplicate API calls
  const isClientFetchingRef = useRef(false);
  const isSetShiftFetchingRef = useRef(false);
  const isAdditionalFetchingRef = useRef(false);

  const clientRowsPerPage = 25;
  const setShiftRowsPerPage = 25;
  const additionalRowsPerPage = 25;

  // Filter state
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [shift, setShift] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<IOptionType[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isClientSearchSelected, setIsClientSearchSelected] = useState(false);

  // BT search state
  const [btSearch, setBtSearch] = useState('');
  const [btSearchSuggestions, setBtSearchSuggestions] = useState<IOptionType[]>(
    []
  );
  const [isBtSearchLoading, setIsBtSearchLoading] = useState(false);
  const [btSearchOpen, setBtSearchOpen] = useState(false);
  const [isBtSearchSelected, setIsBtSearchSelected] = useState(false);
  const [btId, setBtId] = useState<number>();
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<IAppliedFilters>({
    clinicIds: [],
    setShiftBlocks: [],
    clientId: null,
    btId: null,
    isUnscheduledSetBlocks: true,
    isPartiallyScheduledSetBlocks: true,
    carePlanstatus: [],
    checkedInsuranceCategories: [],
    checkedInsurancePayors: [],
    checkedInsurancePlans: [],
    isWaitlistReason: [],
    isJumpstarter: null,
    isVBC: null
  });
  const [filterData, setFilterData] = useState<IFilterData>({});
  const [clinicOptionsList, setClinicOptionsList] = useState<
    IClinicOptionsList[]
  >([]);

  const [selectedClients, setSelectedClients] = useState<IClientRow[]>([]);
  const [selectedSetShifts, setSelectedSetShifts] = useState<ISetShiftRow[]>(
    []
  );
  const [selectedAdditionals, setSelectedAdditionals] = useState<
    IAdditionalRow[]
  >([]);
  const [isScheduleButtonEnabled, setIsScheduleButtonEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Keys to force DataGrid re-mount and reset internal state
  const [clientGridKey, setClientGridKey] = useState(0);
  const [setShiftGridKey, setSetShiftGridKey] = useState(0);
  const [additionalGridKey, setAdditionalGridKey] = useState(0);
  useEffect(() => {
    setErrorMessage('');
    if (
      ((selectedSetShifts.length === 0 && selectedAdditionals.length === 1) ||
        (selectedSetShifts.length === 1 && selectedAdditionals.length === 0)) &&
      selectedClients.length === 1
    ) {
      setIsScheduleButtonEnabled(true);
    } else {
      setIsScheduleButtonEnabled(false);
    }
  }, [
    setIsScheduleButtonEnabled,
    selectedSetShifts,
    selectedAdditionals,
    selectedClients
  ]);

  const { loading: isLoading } = useQuery(GET_WAITLIST_INITIAL_LOAD_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      let {
        clinicOptionsList,
        waitListStatus,
        waitListReasons,
        insuranceCategories,
        insurancePayors,
        insurancePlans
      } = data.getWaitListIntialLoadData.filterList;
      setClinicOptionsList(clinicOptionsList);

      setFilterData({
        waitListStatus,
        waitListReasons,
        insuranceCategories,
        insurancePayors,
        insurancePlans
      });
    },
    onError: error => {
      console.log(error, 'Error while getting location');
    }
  });

  const {
    data: shiftDropdownOptions,
    loading: isLoadingShiftDropdownOptions
  } = useQuery(GET_SHIFT_DROPDOWN_OPTIONS);

  const shiftOptions = useMemo(() => {
    return (shiftDropdownOptions?.getShiftDropdownOptions as ISchedulingShiftDropdownOptions[])?.map(
      item => ({
        label: item.description,
        value: item.shiftId.toString(),
        blockIds: item.blocks.map(block => {
          return block.id;
        })
      })
    );
  }, [shiftDropdownOptions]);

  const [doClientRequest, { data: clientLists }] = useLazyQuery(GET_ATTENDEES, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const nonDisplayedAttendees = clientLists?.attendees?.sort(
        (a1: IOptionType, a2: IOptionType) =>
          alphabeticalSort(a1.displayName, a2.displayName)
      );
      if (nonDisplayedAttendees && nonDisplayedAttendees.length > 0) {
        setSearchSuggestions(nonDisplayedAttendees);
      } else {
        setSearchSuggestions([
          { displayName: 'No BTs found', resultStatus: '__NO_RESULTS__' }
        ]);
      }
      setIsSearchLoading(false);
    }
  });

  const [fetchBTSearchSuggestions, { data: btLists }] = useLazyQuery(
    GET_ATTENDEES,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        const nonDisplayedAttendees = btLists?.attendees?.sort(
          (a1: IOptionType, a2: IOptionType) =>
            alphabeticalSort(a1.displayName, a2.displayName)
        );
        if (nonDisplayedAttendees && nonDisplayedAttendees.length > 0) {
          setBtSearchSuggestions(nonDisplayedAttendees);
        } else {
          setBtSearchSuggestions([
            { displayName: 'No BTs found', resultStatus: '__NO_RESULTS__' }
          ]);
        }
        setIsBtSearchLoading(false);
      }
    }
  );

  const [fetchClientData, { loading: isClientLoading }] = useLazyQuery(
    GET_CARECONNECT_CLIENTS,
    {
      fetchPolicy: 'network-only',
      onCompleted: data => {
        const rawSchedules =
          data?.getClientScheduleSummaries?.clientSchedules || [];
        const schedulesWithRemaining = rawSchedules.filter(
          (schedule: IGraphQLClientData) =>
            Array.isArray(schedule.remainingSchedule) &&
            schedule.remainingSchedule.length > 0
        );
        const removedCount =
          rawSchedules.length - schedulesWithRemaining.length;
        const transformedData = transformClientDataToTableData(
          schedulesWithRemaining
        );

        // If it's the first page (page 0), replace the data
        // If it's a subsequent page, append to existing data
        setClientData(prevData =>
          clientPage === 0 ? transformedData : [...prevData, ...transformedData]
        );

        // Update hasMore based on total count vs current data length
        const totalItems = Math.max(
          0,
          (data?.getClientScheduleSummaries?.count || 0) - removedCount
        );
        setClientTotalCount(totalItems);
        setClientLoading(false);

        const clientCurrentItems =
          clientPage === 0
            ? transformedData.length
            : clientPage * clientRowsPerPage + transformedData.length;
        setClientHasMore(clientCurrentItems < totalItems);

        // Reset fetching ref to allow next pagination
        isClientFetchingRef.current = false;
      },
      onError: error => {
        console.error('Client data query error:', error);
        setClientLoading(false);
        isClientFetchingRef.current = false;
      }
    }
  );

  const [
    createSFCase,
    { loading: isCreateSFCaseFromCareConnectLoading }
  ] = useMutation(CREATE_SALESFORCE_CASE_FROM_CARE_CONNECT, {
    onCompleted: () => {
      console.log('Salesforce case created');
    }
  });
  const [
    fetchSetShiftProviderScheduleSummaries,
    { data: providerScheduleSummaries, loading: isSetShiftLoading }
  ] = useLazyQuery(GET_PROVIDER_SCHEDULE_SUMMARY, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const providerSchedules =
        providerScheduleSummaries?.getProviderScheduleSummaries
          ?.providerSchedules;
      const count =
        providerScheduleSummaries?.getProviderScheduleSummaries?.count;

      const setShiftData =
        providerSchedules?.map(
          (providerSchedule: IProviderScheduleSummary) => ({
            bt: btLabelOf(providerSchedule.provider),
            btLegalName: btLegalLabelOf(providerSchedule.provider),
            acceptedSetShift: providerSchedule.acceptedSetShift.map(
              (shift: IShift) => `${shift.dayOfWeek}\n${shift.times}`
            ),
            remainingSetShift: (
              providerSchedule.remainingSetShiftHoursForUI ||
              providerSchedule.remainingSetShiftHours
            ).map(
              (shift: IShift) => ({ text: `${shift.dayOfWeek}\n${shift.times}`, opensOn: shift.opensOn })
            ),
            remainingSetShiftHoursRaw: providerSchedule.remainingSetShiftHoursRaw!,
            remainingAdditionalShiftHoursRaw: providerSchedule.remainingAdditionalShiftHoursRaw!,
            btId: providerSchedule.provider.id,
            clinic: providerSchedule.clinic
          })
        ) || [];

      setSetShiftData(prevData =>
        setShiftPage === 0 ? setShiftData : [...prevData, ...setShiftData]
      );
      setSetShiftLoading(false);

      const totalItems = count || 0;
      const setShiftCurrentItems =
        setShiftPage === 0
          ? setShiftData.length
          : setShiftPage * setShiftRowsPerPage + setShiftData.length;
      setSetShiftHasMore(setShiftCurrentItems < totalItems);

      // Reset fetching ref to allow next pagination
      isSetShiftFetchingRef.current = false;
    },
    onError: error => {
      console.error('Set shift data query error:', error);
      setSetShiftLoading(false);
      isSetShiftFetchingRef.current = false;
    }
  });

  const clinicsMap = useMemo(() => {
    const map = new Map();
    (clinicOptionsList as IClinicOptionsList[])?.forEach(item => {
      map.set(item.id, {
        id: item.id,
        name: item.name,
        abbreviation: item.abbreviation,
        state: item.state
      });
    });
    return map;
  }, [clinicOptionsList]);

  const clinicOptions = Array.from(clinicsMap?.values() || []).map(c => ({
    label:
      c.abbreviation && c.abbreviation !== '' && c.abbreviation !== null
        ? c.name + ' (' + c.abbreviation + ')'
        : c.name,
    value: c.id.toString()
  }));

  const handleClinicApply = useCallback((selected: string[]) => {
    setSelectedClinics(selected);
    setAppliedFilters(prev => ({
      ...prev,
      clinicIds: selected
    }));
  }, []);

  const handleShiftApply = useCallback((selected: string[]) => {
    setShift(selected);
    setAppliedFilters(prev => ({
      ...prev,
      setShiftBlocks: selected
    }));
  }, []);

  const [
    fetchAdditionalProviderScheduleSummaries,
    { data: additionalProviderScheduleSummaries, loading: isAdditionalLoading }
  ] = useLazyQuery(GET_PROVIDER_SCHEDULE_SUMMARY, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const providerSchedules =
        additionalProviderScheduleSummaries?.getProviderScheduleSummaries
          ?.providerSchedules;
      const count =
        additionalProviderScheduleSummaries?.getProviderScheduleSummaries
          ?.count;

      // Process data for additional table
      const additionalData =
        providerSchedules?.map(
          (providerSchedule: IProviderScheduleSummary) => ({
            bt: btLabelOf(providerSchedule.provider),
            btLegalName: btLegalLabelOf(providerSchedule.provider),
            acceptedAdditional: providerSchedule.acceptedAdditionalShift.map(
              (shift: IShift) => `${shift.dayOfWeek}\n${shift.times}`
            ),
            remainingAdditional: (
              providerSchedule.remainingAdditionalShiftHoursForUI ||
              providerSchedule.remainingAdditionalShiftHours
            ).map(
              (shift: IShift) => ({ text: `${shift.dayOfWeek}\n${shift.times}`, opensOn: shift.opensOn })
            ),
            remainingAdditionalHoursRaw: providerSchedule.remainingAdditionalShiftHoursRaw!,
            btId: providerSchedule.provider.id,
            clinic: providerSchedule.clinic
          })
        ) || [];

      setAdditionalData(prevData =>
        additionalPage === 0 ? additionalData : [...prevData, ...additionalData]
      );
      setAdditionalLoading(false);

      const totalItems = count || 0;
      const additionalCurrentItems =
        additionalPage === 0
          ? additionalData.length
          : additionalPage * additionalRowsPerPage + additionalData.length;
      setAdditionalHasMore(additionalCurrentItems < totalItems);

      // Reset fetching ref to allow next pagination
      isAdditionalFetchingRef.current = false;
    },
    onError: error => {
      console.error('Additional data query error:', error);
      setAdditionalLoading(false);
      isAdditionalFetchingRef.current = false;
    }
  });

  const handleClearClientSearch = useCallback(() => {
    setSearch('');
    setIsClientSearchSelected(false);
    setSearchSuggestions([]);
    setSearchOpen(false);
    setAppliedFilters(prev => ({
      ...prev,
      clientId: null
    }));
  }, []);

  const handleClearBTSearch = useCallback(() => {
    setBtSearch('');
    setBtId(undefined);
    setIsBtSearchSelected(false);
    setBtSearchSuggestions([]);
    setBtSearchOpen(false);
    setAppliedFilters(prev => ({
      ...prev,
      btId: null
    }));
  }, []);

  const handleClientInputChange = useCallback(
    (_: IAutocompleteEvent, value: string, reason: string) => {
      if (reason === 'input') {
        setSearch(value);
        setIsClientSearchSelected(false);
        if (value.length >= 3) {
          setIsSearchLoading(true);
          setSearchOpen(true);
          doClientRequest({
            variables: {
              name: value?.trim(),
              isClient: true,
              shouldExcludeActive: true
            }
          });
        } else {
          setSearchSuggestions([]);
          setSearchOpen(false);
        }
      }
    },
    [doClientRequest]
  );

  const handleClientChange = useCallback(
    (_: IAutocompleteEvent, value: IOptionType | string | null | number) => {
      if (
        value &&
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        value.resultStatus !== '__NO_RESULTS__'
      ) {
        setSearch(value.displayName);
        // setClientId(value.id || null);
        setIsClientSearchSelected(true);
        setAppliedFilters(prev => ({
          ...prev,
          clientId: value.id
        }));
      } else {
        setIsClientSearchSelected(false);
        setAppliedFilters(prev => ({
          ...prev,
          clientId: null
        }));
      }
    },
    []
  );

  const handleBTInputChange = useCallback(
    (_: IAutocompleteEvent, value: string, reason: string) => {
      if (reason === 'input') {
        setBtSearch(value);
        setIsBtSearchSelected(false);
        if (value.length >= 3) {
          setIsBtSearchLoading(true);
          setBtSearchOpen(true);
          fetchBTSearchSuggestions({
            variables: {
              name: value,
              isClient: false,
              isStaffPage: true
            }
          });
        } else {
          setBtSearchSuggestions([]);
          setBtSearchOpen(false);
          setIsBtSearchLoading(false);
          setBtId(undefined);
        }
      }
    },
    [fetchBTSearchSuggestions]
  );

  const handleBTChange = useCallback(
    (_: IAutocompleteEvent, value: IOptionType | string | null) => {
      if (
        value &&
        typeof value !== 'string' &&
        value.resultStatus !== '__NO_RESULTS__'
      ) {
        setBtSearch(value.displayName);
        setBtId(value.id);
        setIsBtSearchSelected(true);
        setAppliedFilters(prev => ({
          ...prev,
          btId: value.id
        }));
      } else {
        setIsBtSearchSelected(false);
        setBtId(undefined);
        setAppliedFilters(prev => ({
          ...prev,
          btId: null
        }));
      }
    },
    []
  );

  const fetchClientBackendData = useCallback(
    (appliedFilters: IAppliedFilters, pageOverride?: number) => {
      const selectedBlockIds = shift.flatMap(shiftId => {
        const selectedShift = shiftOptions.find(
          option => option.value === shiftId
        );

        return selectedShift?.blockIds || [];
      });

      // Get waitlist reason
      const waitlistReasons = appliedFilters.isWaitlistReason;

      setClientLoading(true);
      fetchClientData({
        variables: {
          input: {
            ...(appliedFilters.clinicIds &&
              appliedFilters.clinicIds.length > 0 && {
                clinicIds: appliedFilters.clinicIds?.map(c => Number(c))
              }),
            ...(selectedBlockIds &&
              selectedBlockIds.length > 0 && {
                setShiftBlocks: selectedBlockIds.map(id => id)
              }),
            ...(waitlistReasons &&
              waitlistReasons.length > 0 && {
                isWaitlistReason: waitlistReasons
              }),
            ...(appliedFilters.clientId && {
              clientId: appliedFilters.clientId
            }),
            ...(appliedFilters.carePlanstatus &&
              appliedFilters.carePlanstatus.length > 0 && {
                carePlanstatus: appliedFilters.carePlanstatus
              }),
            ...(appliedFilters.checkedInsuranceCategories &&
              appliedFilters.checkedInsuranceCategories.length > 0 && {
                checkedInsuranceCategories:
                  appliedFilters.checkedInsuranceCategories
              }),
            ...(appliedFilters.checkedInsurancePayors &&
              appliedFilters.checkedInsurancePayors.length > 0 && {
                checkedInsurancePayors: appliedFilters.checkedInsurancePayors
              }),
            ...(appliedFilters.checkedInsurancePlans &&
              appliedFilters.checkedInsurancePlans.length > 0 && {
                checkedInsurancePlans: appliedFilters.checkedInsurancePlans
              }),
            ...(appliedFilters.isJumpstarter !== null && {
              isJumpstarter: appliedFilters.isJumpstarter
            }),
            ...(appliedFilters.isVBC !== null && {
              isVBC: appliedFilters.isVBC
            }),
            ...(appliedFilters.recommendedHoursRange &&
              (appliedFilters.recommendedHoursRange[0] !==
                RECOMMENDED_HOURS_RANGE[0] ||
                appliedFilters.recommendedHoursRange[1] !==
                  RECOMMENDED_HOURS_RANGE[1]) && {
                recommendedHoursRange: appliedFilters.recommendedHoursRange
              }),
            isUnscheduledSetBlocks: true,
            isPartiallyScheduledSetBlocks: true,
            page: pageOverride !== undefined ? pageOverride : clientPage,
            pageSize: clientRowsPerPage
          }
        }
      });
    },
    [
      appliedFilters,
      clientPage,
      clientRowsPerPage,
      fetchClientData,
      shift,
      shiftOptions
    ]
  );

  const hasAnyFilterSelected = useMemo(() => {
    return (
      selectedClinics.length > 0 ||
      shift.length > 0 ||
      isClientSearchSelected ||
      isBtSearchSelected ||
      (appliedFilters.carePlanstatus &&
        appliedFilters.carePlanstatus.length > 0) ||
      (appliedFilters.checkedInsuranceCategories &&
        appliedFilters.checkedInsuranceCategories.length > 0) ||
      (appliedFilters.checkedInsurancePayors &&
        appliedFilters.checkedInsurancePayors.length > 0) ||
      (appliedFilters.checkedInsurancePlans &&
        appliedFilters.checkedInsurancePlans.length > 0) ||
      (appliedFilters.isWaitlistReason &&
        appliedFilters.isWaitlistReason.length > 0) ||
      (appliedFilters.isJumpstarter !== null &&
        appliedFilters.isJumpstarter !== undefined) ||
      appliedFilters.isVBC !== null ||
      (appliedFilters.recommendedHoursRange &&
        (appliedFilters.recommendedHoursRange[0] !==
          RECOMMENDED_HOURS_RANGE[0] ||
          appliedFilters.recommendedHoursRange[1] !==
            RECOMMENDED_HOURS_RANGE[1]))
    );
  }, [
    selectedClinics,
    shift,
    isClientSearchSelected,
    isBtSearchSelected,
    appliedFilters
  ]);

  const hasNonDTFilterSelected = useMemo(() => {
    return (
      selectedClinics.length > 0 ||
      shift.length > 0 ||
      isClientSearchSelected ||
      (appliedFilters.carePlanstatus &&
        appliedFilters.carePlanstatus.length > 0) ||
      (appliedFilters.checkedInsuranceCategories &&
        appliedFilters.checkedInsuranceCategories.length > 0) ||
      (appliedFilters.checkedInsurancePayors &&
        appliedFilters.checkedInsurancePayors.length > 0) ||
      (appliedFilters.checkedInsurancePlans &&
        appliedFilters.checkedInsurancePlans.length > 0) ||
      (appliedFilters.isWaitlistReason &&
        appliedFilters.isWaitlistReason.length > 0) ||
      (appliedFilters.isJumpstarter !== null &&
        appliedFilters.isJumpstarter !== undefined) ||
      appliedFilters.isVBC !== null ||
      (appliedFilters.recommendedHoursRange &&
        (appliedFilters.recommendedHoursRange[0] !==
          RECOMMENDED_HOURS_RANGE[0] ||
          appliedFilters.recommendedHoursRange[1] !==
            RECOMMENDED_HOURS_RANGE[1]))
    );
  }, [selectedClinics, shift, isClientSearchSelected, appliedFilters]);

  const hasBTAPITriggerFilterSelected = useMemo(() => {
    return selectedClinics.length > 0 || shift.length > 0 || isBtSearchSelected;
  }, [selectedClinics, shift, isBtSearchSelected]);

  const fetchSetShiftBackendData = useCallback(() => {
    if (
      appliedFilters.btId ||
      (appliedFilters.clinicIds && appliedFilters.clinicIds.length > 0) ||
      (appliedFilters.setShiftBlocks &&
        appliedFilters.setShiftBlocks.length > 0)
    ) {
      setSetShiftLoading(true);
      fetchSetShiftProviderScheduleSummaries({
        variables: {
          input: {
            providerId: appliedFilters.btId,
            clinicIds: appliedFilters.clinicIds?.map(c => Number(c)) || [],
            setShiftIds:
              appliedFilters.setShiftBlocks?.map(s => Number(s)) || [],
            isUnscheduledSetShift: true,
            isPartiallyScheduledSetShift: true,
            isUnscheduledAdditionalHours: true,
            isPartiallyScheduledAdditionalHours: true,
            searchSetShiftsOnly: true,
            page: setShiftPage,
            pageSize: setShiftRowsPerPage
          }
        }
      });
    }
  }, [
    appliedFilters,
    setShiftPage,
    setShiftRowsPerPage,
    fetchSetShiftProviderScheduleSummaries
  ]);

  const fetchAdditionalBackendData = useCallback(() => {
    if (
      appliedFilters.btId ||
      (appliedFilters.clinicIds && appliedFilters.clinicIds.length > 0) ||
      (appliedFilters.setShiftBlocks &&
        appliedFilters.setShiftBlocks.length > 0)
    ) {
      setAdditionalLoading(true);
      fetchAdditionalProviderScheduleSummaries({
        variables: {
          input: {
            providerId: appliedFilters.btId,
            clinicIds: appliedFilters.clinicIds?.map(c => Number(c)) || [],
            setShiftIds:
              appliedFilters.setShiftBlocks?.map(s => Number(s)) || [],
            isUnscheduledSetShift: true,
            isPartiallyScheduledSetShift: true,
            isUnscheduledAdditionalHours: true,
            isPartiallyScheduledAdditionalHours: true,
            searchAdditionalShiftsOnly: true,
            page: additionalPage,
            pageSize: additionalRowsPerPage
          }
        }
      });
    }
  }, [
    appliedFilters,
    additionalPage,
    additionalRowsPerPage,
    fetchAdditionalProviderScheduleSummaries
  ]);

  const handleRemoveFilter = useCallback(
    (filterType: string, value?: string) => {
      setAppliedFilters(prev => {
        const newFilters = { ...prev };

        switch (filterType) {
          case 'carePlanstatus':
            if (value) {
              newFilters.carePlanstatus =
                prev.carePlanstatus?.filter(v => v !== value) || [];
            } else {
              newFilters.carePlanstatus = [];
            }
            break;
          case 'isWaitlistReason':
            if (value) {
              newFilters.isWaitlistReason =
                prev.isWaitlistReason?.filter((v: string) => v !== value) || [];
            } else {
              newFilters.isWaitlistReason = [];
            }
            break;
          case 'isJumpstarter':
            newFilters.isJumpstarter = null;
            break;
          case 'isVBC':
            newFilters.isVBC = null;
            newFilters.checkedInsuranceCategories = [];
            newFilters.checkedInsurancePayors = [];
            newFilters.checkedInsurancePlans = [];
            break;
          case 'checkedInsuranceCategories':
            if (value) {
              newFilters.checkedInsuranceCategories =
                prev.checkedInsuranceCategories?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsuranceCategories = [];
            }
            break;
          case 'checkedInsurancePayors':
            if (value) {
              newFilters.checkedInsurancePayors =
                prev.checkedInsurancePayors?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsurancePayors = [];
            }
            break;
          case 'checkedInsurancePlans':
            if (value) {
              newFilters.checkedInsurancePlans =
                prev.checkedInsurancePlans?.filter(v => v !== value) || [];
            } else {
              newFilters.checkedInsurancePlans = [];
            }
            break;
          case 'recommendedHoursRange':
            newFilters.recommendedHoursRange = RECOMMENDED_HOURS_RANGE;
            break;
        }

        return newFilters;
      });
    },
    [
      hasAnyFilterSelected,
      fetchClientBackendData,
      selectedClinics,
      shift,
      isClientSearchSelected,
      isBtSearchSelected
    ]
  );

  useEffect(() => {
    const hasClientFilters =
      isClientSearchSelected ||
      (appliedFilters.carePlanstatus &&
        appliedFilters.carePlanstatus.length > 0) ||
      (appliedFilters.checkedInsuranceCategories &&
        appliedFilters.checkedInsuranceCategories.length > 0) ||
      (appliedFilters.checkedInsurancePayors &&
        appliedFilters.checkedInsurancePayors.length > 0) ||
      (appliedFilters.checkedInsurancePlans &&
        appliedFilters.checkedInsurancePlans.length > 0) ||
      (appliedFilters.isWaitlistReason &&
        appliedFilters.isWaitlistReason.length > 0) ||
      (appliedFilters.isJumpstarter !== null &&
        appliedFilters.isJumpstarter !== undefined) ||
      appliedFilters.isVBC !== null ||
      (appliedFilters.recommendedHoursRange &&
        (appliedFilters.recommendedHoursRange[0] !==
          RECOMMENDED_HOURS_RANGE[0] ||
          appliedFilters.recommendedHoursRange[1] !==
            RECOMMENDED_HOURS_RANGE[1]));
    const hasBaseFilters = selectedClinics.length > 0 || shift.length > 0;

    setClientPage(0);
    setClientData([]);
    setSelectedClients([]);
    isClientFetchingRef.current = false;

    if (hasClientFilters || hasBaseFilters) {
      fetchClientBackendData(appliedFilters, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClientSearchSelected,
    appliedFilters.carePlanstatus,
    appliedFilters.checkedInsuranceCategories,
    appliedFilters.checkedInsurancePayors,
    appliedFilters.checkedInsurancePlans,
    appliedFilters.isWaitlistReason,
    appliedFilters.isJumpstarter,
    appliedFilters.isVBC,
    appliedFilters.recommendedHoursRange,
    selectedClinics,
    shift
  ]);

  useEffect(() => {
    if (isBtSearchSelected) {
      setSetShiftPage(0);
      setSetShiftData([]);
      setAdditionalPage(0);
      setAdditionalData([]);
      setSetShiftHasMore(false);
      setAdditionalHasMore(false);
      setSelectedSetShifts([]);
      setSelectedAdditionals([]);
      isSetShiftFetchingRef.current = false;
      isAdditionalFetchingRef.current = false;
      fetchSetShiftBackendData();
      fetchAdditionalBackendData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBtSearchSelected, selectedClinics, shift]);

  useEffect(() => {
    if (selectedClinics.length > 0 || shift.length > 0) {
      if (!isBtSearchSelected) {
        setClientPage(0);
        setClientData([]);
        setClientHasMore(false);
        setSelectedClients([]);
        isClientFetchingRef.current = false;
        fetchClientBackendData(appliedFilters, 0);
      }
      setSetShiftPage(0);
      setSetShiftData([]);
      setAdditionalPage(0);
      setAdditionalData([]);
      setSetShiftHasMore(false);
      setAdditionalHasMore(false);
      setSelectedSetShifts([]);
      setSelectedAdditionals([]);
      isSetShiftFetchingRef.current = false;
      isAdditionalFetchingRef.current = false;
      fetchSetShiftBackendData();
      fetchAdditionalBackendData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinics, shift, isClientSearchSelected, isBtSearchSelected]);

  useEffect(() => {
    if (!hasAnyFilterSelected) {
      setClientData([]);
      setSetShiftData([]);
      setAdditionalData([]);
      setClientLoading(false);
      setSetShiftLoading(false);
      setAdditionalLoading(false);
      setClientHasMore(false);
      setSetShiftHasMore(false);
      setAdditionalHasMore(false);
    }
  }, [hasAnyFilterSelected]);

  useEffect(() => {
    if (!hasBTAPITriggerFilterSelected) {
      setSetShiftData([]);
      setAdditionalData([]);
      setSetShiftLoading(false);
      setAdditionalLoading(false);
      setSetShiftHasMore(false);
      setAdditionalHasMore(false);
    }
  }, [hasBTAPITriggerFilterSelected]);

  useEffect(() => {
    if (!hasNonDTFilterSelected) {
      setClientData([]);
      setClientLoading(false);
      setClientHasMore(false);
    }
  }, [hasNonDTFilterSelected]);

  const handleShowAdditionalFilters = useCallback((show: boolean) => {
    setShowAdditionalFilters(show);
  }, []);

  const handleAdditionalFiltersChange = useCallback(
    (filters: Partial<IAppliedFilters>) => {
      setAppliedFilters({
        clinicIds: appliedFilters.clinicIds,
        setShiftBlocks: appliedFilters.setShiftBlocks,
        clientId: appliedFilters.clientId,
        btId: appliedFilters.btId,
        isUnscheduledSetBlocks: filters.isUnscheduledSetBlocks ?? true,
        isPartiallyScheduledSetBlocks:
          filters.isPartiallyScheduledSetBlocks ?? true,
        carePlanstatus: filters.carePlanstatus || [],
        checkedInsuranceCategories: filters.checkedInsuranceCategories || [],
        checkedInsurancePayors: filters.checkedInsurancePayors || [],
        checkedInsurancePlans: filters.checkedInsurancePlans || [],
        isWaitlistReason: filters.isWaitlistReason || [],
        isJumpstarter: filters.isJumpstarter ?? null,
        isVBC: filters.isVBC ?? null,
        recommendedHoursRange:
          filters.recommendedHoursRange || RECOMMENDED_HOURS_RANGE
      });
    },
    [
      selectedClinics,
      shift,
      search,
      btId,
      isClientSearchSelected,
      isBtSearchSelected,
      appliedFilters,
      fetchClientBackendData
    ]
  );

  function formatBlocks(blocks: string, isClient: boolean) {
    let counter = 0;
    return blocks.replace(/\n/g, () => {
      counter++;
      return counter % 2 === 0 && isClient ? ', ' : ' ';
    });
  }

  function formatBtInformation(btDataArray: IBTData[]) {
    return btDataArray
      .map(
        (btData, index) =>
          ` ${index + 1}. ${btData.bt}, ${
            btData.remainingSetShift?.length! > 0
              ? `Set Shift: ${formatBlocks(
                  btData.acceptedSetShift?.join(', ')!,
                  false
                )}, Remaining Set Shift: ${formatBlocks(
                  btData.remainingSetShift?.map((i: any) => typeof i === 'string' ? i : i.text).join(', ')!,
                  false
                )}`
              : ''
          }${
            btData.remainingSetShift?.length! > 0 &&
            btData.remainingAdditional?.length! > 0
              ? `, `
              : ''
          }${
            btData.remainingAdditional?.length! > 0
              ? `Accepted Additional: ${formatBlocks(
                  btData.acceptedAdditional?.join(', ')!,
                  false
                )}, Remaining Additional: ${formatBlocks(
                  btData.remainingAdditional?.map((i: any) => typeof i === 'string' ? i : i.text).join(', ')!,
                  false
                )}`
              : ''
          } `
      )
      .join('\n');
  }

  const handleCreateCase = useCallback(async () => {
    const btDataMap = new Map<number, IBTData>();
    selectedSetShifts.forEach(obj => {
      btDataMap.set(obj.btId, {
        bt: obj.btLegalName,
        btId: obj.btId,
        clinic: obj.clinic?.name || '',
        acceptedSetShift: obj.acceptedSetShift || [],
        remainingSetShift: (obj.remainingSetShift || []).map((i: any) => typeof i === 'string' ? i : i.text)
      });
    });
    selectedAdditionals.forEach(obj => {
      if (btDataMap.has(obj.btId)) {
        btDataMap.get(obj.btId)!.acceptedAdditional =
          obj.acceptedAdditional || [];
        btDataMap.get(obj.btId)!.remainingAdditional =
          (obj.remainingAdditional || []).map((i: any) => typeof i === 'string' ? i : i.text);
      } else {
        btDataMap.set(obj.btId, {
          bt: obj.btLegalName,
          btId: obj.btId,
          clinic: obj.clinic?.name || '',
          acceptedAdditional: obj.acceptedAdditional || [],
          remainingAdditional: (obj.remainingAdditional || []).map((i: any) => typeof i === 'string' ? i : i.text)
        });
      }
    });
    const btDataArray = Array.from(btDataMap.values());
    if (selectedClients.length === 0 || btDataArray.length === 0) {
      toast.error('No clients or BT data found');
      return;
    }
    const payload: ICreateCasePayload[] = [];
    selectedClients.forEach(client => {
      const clientData = {
        patientName: client.client!,
        clientClinicName: client.clinic?.name!,
        acceptedBlocks: formatBlocks(client.acceptedBlock, true),
        remainingBlocks: formatBlocks(client.remainingBlocks, true),
        waitlistStatus: client.status,
        clientSalesforceId: client.salesForceId!,
        isVBC: client.insurancePlan?.isVBC ?? false,
        btInformation: formatBtInformation(btDataArray)
      };
      payload.push(clientData);
    });
    console.log('handleCreateCase payload', payload);
    try {
      const response = await createSFCase({ variables: { data: payload } });
      if (
        response?.data?.createSalesforceCaseFromCareConnect ===
        'Successfully queued'
      ) {
        toast.success('Case request queued');
      } else {
        toast.error('Failed to queue case');
        console.error(response);
      }
      setSelectedClients([]);
      setSelectedSetShifts([]);
      setSelectedAdditionals([]);

      setClientGridKey(prev => prev + 1);
      setSetShiftGridKey(prev => prev + 1);
      setAdditionalGridKey(prev => prev + 1);
    } catch (e) {
      toast.error('Error sending to mulesoft');
      console.error(e);
    }
  }, [selectedClients, createSFCase, selectedSetShifts, selectedAdditionals]);

  const handleSchedule = useCallback(() => {
    setErrorMessage('');
    const clientsData = Array.from(selectedClients);
    const setShiftsData = Array.from(selectedSetShifts);
    const additionalsData = Array.from(selectedAdditionals);
    let clientId = null;
    let providerId = null;
    let clinicPreference = 'homeClinic';
    let clientClinicId = null;
    let providerClinicId = null;
    let clientAvailableBlocks: DayAvailabilityBlock[] = [];
    let providerAvailableBlocks: DayAvailabilityBlock[] = [];
    let clientTimezone = 'America/Los_Angeles';

    if (clientsData.length > 0) {
      clientId = clientsData[0].clientId;
      clientAvailableBlocks = clientsData[0].remainingBlocksRaw || [];

      const selectedClient = selectedClients[0];
      if (
        selectedClient &&
        selectedClient.clinic &&
        selectedClient.clinic.timezone
      ) {
        clientTimezone = selectedClient.clinic.timezone;
        clientClinicId = selectedClient.clinic.id;
      }
    }
    if (setShiftsData.length > 0) {
      providerId = setShiftsData[0].btId;
      providerAvailableBlocks =
        setShiftsData[0].remainingSetShiftHoursRaw || [];
      providerClinicId = setShiftsData[0].clinic?.id;
    } else if (additionalsData.length > 0) {
      providerId = additionalsData[0].btId;
      providerAvailableBlocks =
        additionalsData[0].remainingAdditionalHoursRaw || [];
      providerClinicId = additionalsData[0].clinic?.id;
    }
    const intersectingTimeBlocks = getIntersectingTimeBlocks(
      clientAvailableBlocks,
      providerAvailableBlocks
    );
    if (providerClinicId && clientClinicId) {
      clinicPreference =
        providerClinicId !== clientClinicId ? 'allClinics' : 'homeClinic';
    }
    if (
      intersectingTimeBlocks.startTime &&
      intersectingTimeBlocks.endTime &&
      intersectingTimeBlocks.days
    ) {
      const convertedStartTime = convertTimeToLocalBrowser(
        intersectingTimeBlocks.startTime,
        clientTimezone
      );
      const convertedEndTime = convertTimeToLocalBrowser(
        intersectingTimeBlocks.endTime,
        clientTimezone
      );
      const schedulingUrl =
        ROUTE_PATHS.NEW_APPOINTMENT +
        ROUTE_PARAMS.CLIENT +
        clientId +
        ROUTE_PARAMS.CARE_CONNECT_PROVIDER +
        providerId +
        ROUTE_PARAMS.CARE_CONNECT_PARENT_TYPE_ID +
        ABA_APPT_IDS_FOR_MATCHES[process.env.REACT_APP_STAGE!] + // Default appointment type ID
        ROUTE_PARAMS.CARE_CONNECT_START_TIME +
        convertedStartTime +
        ROUTE_PARAMS.CARE_CONNECT_END_TIME +
        convertedEndTime +
        ROUTE_PARAMS.CARE_CONNECT_LOCATION +
        'In Clinic' +
        ROUTE_PARAMS.CARE_CONNECT_CLINIC +
        clinicPreference +
        ROUTE_PARAMS.CARE_CONNECT_RECURRING_EVERY +
        'custom' +
        ROUTE_PARAMS.CARE_CONNECT_RECURRING_DAYS_ARRAY +
        intersectingTimeBlocks.days +
        ROUTE_PARAMS.PLANNER_VIEW +
        'planner week';
      window.open(schedulingUrl, '_blank');
      setSelectedClients([]);
      setSelectedSetShifts([]);
      setSelectedAdditionals([]);

      // Reset DataGrid keys to force re-mount and uncheck all checkboxes
      setClientGridKey(prev => prev + 1);
      setSetShiftGridKey(prev => prev + 1);
      setAdditionalGridKey(prev => prev + 1);
    } else {
      setErrorMessage('No common time blocks found for scheduling');
    }
  }, [selectedClients, selectedSetShifts, selectedAdditionals]);

  const {
    clientColumns,
    setShiftColumns,
    additionalColumns
  } = useMatchesTabColumns();

  // Infinite scroll for client table
  useEffect(() => {
    const scrollContainer = document.querySelector(
      '[data-client-scroll]'
    ) as HTMLElement;
    if (scrollContainer) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          clientHasMore &&
          !clientLoading &&
          !isClientFetchingRef.current
        ) {
          isClientFetchingRef.current = true;
          setClientPage(prev => prev + 1);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [clientHasMore, clientLoading, setClientPage]);

  useEffect(() => {
    const scrollContainer = document.querySelector(
      '[data-setshift-scroll]'
    ) as HTMLElement;
    if (scrollContainer) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          setShiftHasMore &&
          !setShiftLoading &&
          !isSetShiftFetchingRef.current
        ) {
          isSetShiftFetchingRef.current = true;
          setSetShiftPage(prev => prev + 1);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
    return undefined; // Return undefined when scrollContainer is not found
  }, [setShiftHasMore, setShiftLoading]);

  useEffect(() => {
    const scrollContainer = document.querySelector(
      '[data-additional-scroll]'
    ) as HTMLElement;
    if (scrollContainer) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          additionalHasMore &&
          !additionalLoading &&
          !isAdditionalFetchingRef.current
        ) {
          isAdditionalFetchingRef.current = true;
          setAdditionalPage(prev => prev + 1);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [additionalHasMore, additionalLoading]);

  // Trigger fetch when set shift page changes
  useEffect(() => {
    if (setShiftPage > 0 && hasBTAPITriggerFilterSelected) {
      fetchSetShiftBackendData();
    } else {
      isSetShiftFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setShiftPage, hasBTAPITriggerFilterSelected]);

  // Trigger fetch when additional page changes
  useEffect(() => {
    if (additionalPage > 0 && hasBTAPITriggerFilterSelected) {
      fetchAdditionalBackendData();
    } else {
      isAdditionalFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalPage, hasBTAPITriggerFilterSelected]);

  // Trigger fetch when client page changes
  useEffect(() => {
    if (clientPage > 0 && hasAnyFilterSelected) {
      fetchClientBackendData(appliedFilters);
    } else {
      isClientFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPage, hasAnyFilterSelected]);

  return (
    <>
      <Box
        className="matches-tab-container"
        style={{ backgroundColor: '#FCFCFC', padding: 20 }}
      >
        <Grid
          container
          className="filterBar"
          alignItems="center"
          // justifyContent="space-between"
          style={{ marginBottom: 20 }}
          spacing={1}
        >
          <Grid item xs={8} sm={8} md={4} lg={2}>
            <Box position="relative">
              <MultiSelectDropdown
                label="Clinic"
                options={clinicOptions}
                selected={selectedClinics}
                onApply={handleClinicApply}
                placeholder={isLoading ? 'Loading clinics...' : 'Clinic'}
                disabled={isLoading}
              />
              {isLoading && (
                <Box
                  position="absolute"
                  top="50%"
                  right={40}
                  style={{ transform: 'translateY(-50%)', zIndex: 1 }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={8} sm={8} md={4} lg={2}>
            <Box position="relative">
              <MultiSelectDropdown
                label="Remaining Shift"
                options={shiftOptions || []}
                selected={shift}
                onApply={handleShiftApply}
                placeholder="Remaining Shift"
                disabled={isLoadingShiftDropdownOptions}
              />
              {isLoadingShiftDropdownOptions && (
                <Box
                  position="absolute"
                  top="50%"
                  right={40}
                  style={{ transform: 'translateY(-50%)', zIndex: 1 }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={8} sm={8} md={4} lg={2}>
            <AutocompleteSearch
              isClient={true}
              placeholder="Search for Client"
              search={search}
              isSearchLoading={isSearchLoading}
              searchSuggestions={searchSuggestions}
              searchOpen={searchOpen}
              isSearchSelected={isClientSearchSelected}
              onInputChange={handleClientInputChange}
              onChange={handleClientChange}
              onClear={handleClearClientSearch}
              onOpen={() => {
                if (search.length >= 3) setSearchOpen(true);
              }}
              onClose={() => setSearchOpen(false)}
            />
          </Grid>
          <Grid item xs={8} sm={8} md={4} lg={2}>
            <AutocompleteSearch
              isClient={false}
              placeholder="Search for BT"
              search={btSearch}
              isSearchLoading={isBtSearchLoading}
              searchSuggestions={btSearchSuggestions}
              searchOpen={btSearchOpen}
              isSearchSelected={isBtSearchSelected}
              onInputChange={handleBTInputChange}
              onChange={handleBTChange}
              onClear={handleClearBTSearch}
              onOpen={() => {
                if (btSearch.length >= 3) setBtSearchOpen(true);
              }}
              onClose={() => setBtSearchOpen(false)}
            />
          </Grid>
          <Grid
            item
            xs={8}
            sm={8}
            md={4}
            lg={3}
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}
          >
            <Typography
              style={{
                cursor: 'pointer',
                color: '#6f42f5',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500
              }}
              onClick={() => handleShowAdditionalFilters(true)}
            >
              <FilterListIcon style={{ marginRight: '8px' }} /> Additional
              Filters
            </Typography>
          </Grid>
        </Grid>

        {/* Display applied filter chips */}
        <FilterChips
          appliedFilters={appliedFilters}
          onRemoveFilter={handleRemoveFilter}
        />

        <Box
          display="flex"
          style={{
            gap: 10,
            width: '100%',
            overflow: 'hidden',
            marginBottom: '80px'
          }}
        >
          <div
            data-client-scroll
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'auto',
              height: '60vh',
              minWidth: '400px'
            }}
          >
            <DataGrid
              key={clientGridKey}
              columns={clientColumns}
              data={clientData}
              total={clientTotalCount}
              page={clientPage + 1}
              pageSize={clientRowsPerPage}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              hidePagination={true}
              loading={isClientLoading}
              selectable={true}
              selectedAll={selectedClients.length > 0}
              rowKey="clientId"
              selectedRows={clientData.filter(row =>
                selectedClients.some(
                  selectedRow => selectedRow.clientId === row.clientId
                )
              )}
              onRowSelect={selectedRows => {
                setSelectedClients(selectedRows);
              }}
              emptyText={
                !hasNonDTFilterSelected
                  ? 'Select at least one filter to show results.'
                  : 'No data available'
              }
            />
            {clientHasMore && (
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                mt={2}
                mb={2}
              >
                {clientLoading && (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={20} />
                    <span>Loading more clients...</span>
                  </Box>
                )}
              </Box>
            )}
          </div>
          <div
            data-setshift-scroll
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'auto',
              height: '60vh'
            }}
          >
            <DataGrid
              key={setShiftGridKey}
              columns={setShiftColumns}
              data={setShiftData}
              total={setShiftData.length}
              page={setShiftPage + 1}
              pageSize={setShiftRowsPerPage}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              hidePagination={true}
              loading={isSetShiftLoading}
              selectable={true}
              selectedAll={selectedSetShifts.length > 0}
              rowKey="btId"
              selectedRows={setShiftData.filter(row =>
                selectedSetShifts.some(
                  selectedRow => selectedRow.btId === row.btId
                )
              )}
              onRowSelect={selectedRows => {
                setSelectedSetShifts(selectedRows);
              }}
              emptyText={
                !hasBTAPITriggerFilterSelected && !isSetShiftLoading
                  ? 'Select at least one filter to show results.'
                  : 'No data available'
              }
            />
            {setShiftHasMore && (
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                mt={2}
                mb={2}
              >
                {setShiftLoading && (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={20} />
                    <span>Loading more set shifts...</span>
                  </Box>
                )}
              </Box>
            )}
          </div>
          <div
            data-additional-scroll
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'auto',
              height: '60vh'
            }}
          >
            <DataGrid
              key={additionalGridKey}
              columns={additionalColumns}
              data={additionalData}
              total={additionalData.length}
              page={additionalPage + 1}
              pageSize={additionalRowsPerPage}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              hidePagination={true}
              loading={isAdditionalLoading}
              selectable={true}
              selectedAll={selectedAdditionals.length > 0}
              rowKey="btId"
              selectedRows={additionalData.filter(row =>
                selectedAdditionals.some(
                  selectedRow => selectedRow.btId === row.btId
                )
              )}
              onRowSelect={selectedRows => {
                setSelectedAdditionals(selectedRows);
              }}
              emptyText={
                !hasBTAPITriggerFilterSelected && !isAdditionalLoading
                  ? 'Select at least one filter to show results.'
                  : 'No data available'
              }
            />
            {additionalHasMore && (
              <Box
                width="100%"
                display="flex"
                justifyContent="center"
                mt={2}
                mb={2}
              >
                {additionalLoading && (
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CircularProgress size={20} />
                    <span>Loading more additional...</span>
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Box>

        {selectedClients.length > 0 &&
          (selectedSetShifts.length > 0 || selectedAdditionals.length > 0) && (
            <Box className="matches-footer">
              <Box>
                <Typography className="matches-footer-title">
                  Create a Case or Schedule Client and BT
                </Typography>
                <Typography className="matches-footer-description">
                  Create a case in SalesForce or schedule the client and BT with
                  the information selected on the table.
                </Typography>
                <Box className="matches-footer-buttons">
                  <Typography
                    className={`matches-footer-button matches-footer-button-filled ${
                      selectedClients.length > 0 &&
                      (selectedSetShifts.length > 0 ||
                        selectedAdditionals.length > 0) &&
                      !isCreateSFCaseFromCareConnectLoading
                        ? ''
                        : 'matches-button-disabled'
                    }`}
                    onClick={() => handleCreateCase()}
                  >
                    Create Case
                  </Typography>
                  <Typography
                    className={`matches-footer-button matches-footer-button-filled ${
                      isScheduleButtonEnabled ? '' : 'matches-button-disabled'
                    }`}
                    onClick={handleSchedule}
                  >
                    Schedule
                  </Typography>
                </Box>
                {errorMessage && (
                  <Typography style={{ marginTop: 10 }}>
                    <span style={{ color: 'red' }}>{errorMessage}</span>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
      </Box>

      <AdditionalFilters
        show={showAdditionalFilters}
        close={() => handleShowAdditionalFilters(false)}
        onFiltersChange={handleAdditionalFiltersChange}
        filterData={filterData}
        isLoading={isLoading}
        appliedFilters={appliedFilters}
        disabledClearFilters={!hasAnyFilterSelected}
      />
    </>
  );
}
